import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { safeNavigate } from '../utils/navigationUtils';
import { motion } from 'framer-motion';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import translations from '../contexts/translations';
import proService from '../services/proService';

const EditListing = ({ language }) => {
  const t = translations[language];
  const navigate = useNavigate();
  const { id } = useParams();
  // const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isProUser, setIsProUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [listing, setListing] = useState({
    type: 'hotel',
    title: '',
    description: '',
    price: '',
    location: '',
    amenities: [], // for hotels
    itinerary: [], // for circuits
    features: [], // for cars
    highlights: [], // for tours
  });
  const [newItem, setNewItem] = useState(''); // For adding amenities, itinerary, features, or highlights

  const fetchListing = useCallback(async () => {
    try {
      setLoading(true);
      const listingData = await proService.getListingById(id);
      setListing({
        type: listingData.type || 'hotel',
        title: listingData.title || '',
        description: listingData.description || '',
        price: listingData.price || '',
        location: listingData.location || '',
        amenities: listingData.amenities || [],
        itinerary: listingData.itinerary || [],
        features: listingData.features || [],
        highlights: listingData.highlights || [],
      });
      setLoading(false);
    } catch (err) {
      setError(t.errorFetchingListing || 'Error loading listing data');
      toast.error(t.errorFetchingListing || 'Error loading listing data');
      setLoading(false);
    }
  }, [id, t]);

  const checkProStatus = useCallback(async () => {
    try {
      const user = await proService.getUserProfile();
      if (user.role !== 'pro') {
        setError(t.notProUser || 'Access restricted to professional users');
        setLoading(false);
        return;
      }
      setIsProUser(true);
      fetchListing();
    } catch (err) {
      setError(t.errorFetchingProfile || 'Error verifying user status');
      toast.error(t.errorFetchingProfile || 'Error verifying user status');
      setLoading(false);
    }
  }, [fetchListing, t]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      safeNavigate(navigate, '/login', { state: { from: `/pro/edit-listing/${id}` } });
      return;
    }
    // setIsAuthenticated(true);
    checkProStatus();
  }, [id, navigate, checkProStatus]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setListing((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemAdd = (field) => {
    if (newItem.trim()) {
      setListing((prev) => ({
        ...prev,
        [field]: [...prev[field], newItem.trim()],
      }));
      setNewItem('');
    }
  };

  const handleItemRemove = (field, index) => {
    setListing((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!listing.title || !listing.description || !listing.price || !listing.location) {
      toast.error(t.fillAllFields || 'Please fill all required fields');
      return;
    }
    if (listing.price < 0) {
      toast.error(t.positivePrice || 'Price must be a positive number');
      return;
    }
    try {
      await proService.updateListing(id, listing);
      toast.success(t.listingUpdated || 'Listing updated successfully');
      navigate('/pro');
    } catch (err) {
      toast.error(t.errorUpdatingListing || 'Error updating listing');
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)' }}
      >
        <div className="text-amber-900">{t.loading || 'Loading...'}</div>
      </div>
    );
  }

  if (error || !isProUser) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)' }}
      >
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl max-w-md">
          {error || t.notProUser || 'Access restricted to professional users'}
          <button
            onClick={() => navigate('/pro')}
            className="mt-4 w-full py-2 px-4 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors"
          >
            {t.backToDashboard || 'Back to Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen py-24 relative overflow-hidden"
      style={{ background: 'linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)' }}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/src/assets/zelij3.png)',
          backgroundRepeat: 'repeat',
          backgroundSize: '400px',
          backgroundAttachment: 'fixed',
          opacity: 0.08,
        }}
      ></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,186,73,0.08),transparent_60%)]"></div>
      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-serif font-extrabold text-amber-900 mb-4">
            {t.editListing || 'Edit Listing'}
          </h1>
          <p className="text-lg max-w-2xl mx-auto leading-relaxed text-amber-900/80">
            {t.editListingDesc || 'Update your listing details to attract more clients.'}
          </p>
        </motion.div>

        {/* Edit Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white/90 border border-amber-100 rounded-3xl shadow-xl p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block mb-2 font-medium text-amber-900">{t.type || 'Type'}</label>
              <select
                name="type"
                value={listing.type}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                disabled
              >
                <option value="hotel">{t.hotel || 'Hotel'}</option>
                <option value="circuit">{t.circuit || 'Circuit'}</option>
                <option value="car">{t.car || 'Car Rental'}</option>
                <option value="tour">{t.tour || 'Guided Tour'}</option>
              </select>
            </div>
            <div>
              <label className="block mb-2 font-medium text-amber-900">{t.title || 'Title'}</label>
              <input
                type="text"
                name="title"
                value={listing.title}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
              />
            </div>
            <div>
              <label className="block mb-2 font-medium text-amber-900">{t.description || 'Description'}</label>
              <textarea
                name="description"
                value={listing.description}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                rows="6"
              ></textarea>
            </div>
            <div>
              <label className="block mb-2 font-medium text-amber-900">{t.price || 'Price (MAD)'}</label>
              <input
                type="number"
                name="price"
                value={listing.price}
                onChange={handleInputChange}
                required
                min="0"
                className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
              />
            </div>
            <div>
              <label className="block mb-2 font-medium text-amber-900">{t.location || 'Location'}</label>
              <input
                type="text"
                name="location"
                value={listing.location}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
              />
            </div>

            {/* Type-Specific Fields */}
            {listing.type === 'hotel' && (
              <div>
                <label className="block mb-2 font-medium text-amber-900">{t.amenities || 'Amenities'}</label>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder={t.addAmenity || 'Add an amenity (e.g., Wi-Fi)'}
                    className="flex-1 px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                  />
                  <motion.button
                    type="button"
                    onClick={() => handleItemAdd('amenities')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all"
                  >
                    <Plus className="h-5 w-5" />
                  </motion.button>
                </div>
                <ul className="space-y-2">
                  {listing.amenities.map((amenity, index) => (
                    <li key={index} className="flex items-center justify-between text-amber-900/80">
                      <span>{amenity}</span>
                      <motion.button
                        type="button"
                        onClick={() => handleItemRemove('amenities', index)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="p-1 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all"
                      >
                        <Trash2 className="h-4 w-4" />
                      </motion.button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {listing.type === 'circuit' && (
              <div>
                <label className="block mb-2 font-medium text-amber-900">{t.itinerary || 'Itinerary'}</label>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder={t.addItinerary || 'Add an itinerary item (e.g., Day 1: Marrakech)'}
                    className="flex-1 px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                  />
                  <motion.button
                    type="button"
                    onClick={() => handleItemAdd('itinerary')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all"
                  >
                    <Plus className="h-5 w-5" />
                  </motion.button>
                </div>
                <ul className="space-y-2">
                  {listing.itinerary.map((item, index) => (
                    <li key={index} className="flex items-center justify-between text-amber-900/80">
                      <span>{item}</span>
                      <motion.button
                        type="button"
                        onClick={() => handleItemRemove('itinerary', index)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="p-1 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all"
                      >
                        <Trash2 className="h-4 w-4" />
                      </motion.button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {listing.type === 'car' && (
              <div>
                <label className="block mb-2 font-medium text-amber-900">{t.features || 'Features'}</label>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder={t.addFeature || 'Add a feature (e.g., Air Conditioning)'}
                    className="flex-1 px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                  />
                  <motion.button
                    type="button"
                    onClick={() => handleItemAdd('features')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all"
                  >
                    <Plus className="h-5 w-5" />
                  </motion.button>
                </div>
                <ul className="space-y-2">
                  {listing.features.map((feature, index) => (
                    <li key={index} className="flex items-center justify-between text-amber-900/80">
                      <span>{feature}</span>
                      <motion.button
                        type="button"
                        onClick={() => handleItemRemove('features', index)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="p-1 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all"
                      >
                        <Trash2 className="h-4 w-4" />
                      </motion.button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {listing.type === 'tour' && (
              <div>
                <label className="block mb-2 font-medium text-amber-900">{t.highlights || 'Highlights'}</label>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    placeholder={t.addHighlight || 'Add a highlight (e.g., Visit Medina)'}
                    className="flex-1 px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                  />
                  <motion.button
                    type="button"
                    onClick={() => handleItemAdd('highlights')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all"
                  >
                    <Plus className="h-5 w-5" />
                  </motion.button>
                </div>
                <ul className="space-y-2">
                  {listing.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-center justify-between text-amber-900/80">
                      <span>{highlight}</span>
                      <motion.button
                        type="button"
                        onClick={() => handleItemRemove('highlights', index)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="p-1 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all"
                      >
                        <Trash2 className="h-4 w-4" />
                      </motion.button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-4">
              <motion.button
                type="submit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-1 py-3 px-6 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 transition-all"
              >
                {t.saveChanges || 'Save Changes'}
              </motion.button>
              <motion.button
                type="button"
                onClick={() => navigate('/pro')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-1 py-3 px-6 bg-amber-100 text-amber-900 rounded-xl font-semibold hover:bg-amber-200 transition-all"
              >
                {t.cancel || 'Cancel'}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default EditListing;